import React, { useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { NavBar } from '@/components/navbar';
import { Footer } from '@/components/SiteFooter';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { ArrowRight, FileText, Lock, Server, Shield, UserCheck, Download } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

const PrivacyPage = () => {
  const { i18n } = useTranslation();
  const currentLang = i18n.language;
  const pageRef = useRef<HTMLDivElement>(null);

  // Scroll-based animations
  const { scrollYProgress } = useScroll({
    target: pageRef,
    offset: ['start start', 'end end'],
  });

  const headerOpacity = useTransform(scrollYProgress, [0, 0.1], [1, 0.9]);

  // Privacy content in both languages
  const privacyContent = {
    en: {
      lastUpdated: "Last Updated: December 22, 2024",
      title: "Our Commitment to",
      titleHighlight: "Privacy and Data Protection",
      subtitle: "We prioritize the security and privacy of your data with the highest standards in the industry",
      quote: '"We follow rigorous data privacy procedures to ensure your data is handled securely and responsibly."',
      overview: {
        paragraph1: "We comply with the EU General Data Protection Regulation (GDPR) and adhere to the requirements of ISO/IEC 27001. Importantly, we never use your data to train large language models (LLMs).",
        paragraph2: "Our platform is developed in Europe and uses Amazon Web Services (AWS) as its hosting provider. Customer data is stored exclusively on ISO/IEC 27001-certified servers, ensuring strong protection against unauthorized access."
      },
      features: {
        transparency: {
          title: "Transparency & Control",
          description: "You remain the sole owner and controller of your data, as outlined in GDPR Article 24."
        },
        infrastructure: {
          title: "Secure Infrastructure", 
          description: "Data stored exclusively on ISO/IEC 27001-certified servers with strong protection."
        },
        compliance: {
          title: "Compliance & Security",
          description: "We follow GDPR and ISO/IEC 27001 standards with regular updates to technical measures."
        }
      },
      detailed: {
        access: {
          title: "Data Access & Control",
          description: "Only authorized personnel can access your data, and only when necessary for specific tasks such as onboarding or support. Your data will not be used for purposes beyond the services you request, including training LLMs."
        },
        policy: {
          title: "Key Policy Points",
          points: [
            "Your data is stored securely on ISO/IEC 27001-certified servers and processed in compliance with GDPR.",
            "You maintain full control of your data, with no unauthorized use, including for LLM training.",
            "We use secure encryption for all data transfers and regular updates to security measures.",
            "Our team is here to help with any data-related questions or concerns."
          ]
        }
      },
      cta: {
        title: "Questions About Our Privacy Policy?",
        description: "If you have any questions or concerns about how we handle your data, our team is ready to assist you.",
        button: "Contact us"
      }
    },
    de: {
      lastUpdated: "Letzte Aktualisierung: 22. Dezember 2024",
      title: "Unser Engagement für",
      titleHighlight: "Datenschutz und Datensicherheit",
      subtitle: "Wir priorisieren die Sicherheit und den Schutz Ihrer Daten mit höchsten Industriestandards",
      quote: '"Wir befolgen strenge Datenschutzverfahren, um sicherzustellen, dass Ihre Daten sicher und verantwortungsvoll behandelt werden."',
      overview: {
        paragraph1: "Wir entsprechen der EU-Datenschutz-Grundverordnung (DSGVO) und halten uns an die Anforderungen von ISO/IEC 27001. Wichtig ist, dass wir Ihre Daten niemals zum Trainieren großer Sprachmodelle (LLMs) verwenden.",
        paragraph2: "Unsere Plattform wird in Europa entwickelt und nutzt Amazon Web Services (AWS) als Hosting-Anbieter. Kundendaten werden ausschließlich auf ISO/IEC 27001-zertifizierten Servern gespeichert und gewährleisten starken Schutz vor unbefugtem Zugriff."
      },
      features: {
        transparency: {
          title: "Transparenz & Kontrolle",
          description: "Sie bleiben der alleinige Eigentümer und Kontroller Ihrer Daten, wie in DSGVO Artikel 24 dargelegt."
        },
        infrastructure: {
          title: "Sichere Infrastruktur",
          description: "Daten werden ausschließlich auf ISO/IEC 27001-zertifizierten Servern mit starkem Schutz gespeichert."
        },
        compliance: {
          title: "Compliance & Sicherheit",
          description: "Wir befolgen DSGVO- und ISO/IEC 27001-Standards mit regelmäßigen Updates der technischen Maßnahmen."
        }
      },
      detailed: {
        access: {
          title: "Datenzugriff & Kontrolle",
          description: "Nur autorisiertes Personal kann auf Ihre Daten zugreifen, und nur wenn es für spezifische Aufgaben wie Onboarding oder Support erforderlich ist. Ihre Daten werden nicht für Zwecke außerhalb der von Ihnen angeforderten Services verwendet, einschließlich LLM-Training."
        },
        policy: {
          title: "Wichtige Richtlinienpunkte",
          points: [
            "Ihre Daten werden sicher auf ISO/IEC 27001-zertifizierten Servern gespeichert und DSGVO-konform verarbeitet.",
            "Sie behalten die volle Kontrolle über Ihre Daten, ohne unbefugte Nutzung, einschließlich für LLM-Training.",
            "Wir verwenden sichere Verschlüsselung für alle Datenübertragungen und regelmäßige Updates der Sicherheitsmaßnahmen.",
            "Unser Team steht Ihnen bei allen datenbezogenen Fragen oder Bedenken zur Verfügung."
          ]
        }
      },
      cta: {
        title: "Fragen zu unserer Datenschutzerklärung?",
        description: "Wenn Sie Fragen oder Bedenken bezüglich unseres Umgangs mit Ihren Daten haben, steht unser Team bereit, Ihnen zu helfen.",
        button: "Kontaktieren Sie uns"
      }
    }
  };

  const content = privacyContent[currentLang as 'en' | 'de'] || privacyContent.en;

  return (
    <div ref={pageRef}>
      <Helmet>
        <title>Privacy Policy | Verdea</title>
        <meta
          name="description"
          content="Learn about Verdea's privacy policy and how we protect your data"
        />
      </Helmet>

      <NavBar />

      <main className="relative pt-24 pb-16 min-h-screen overflow-hidden">
        {/* Background Elements - Green theme */}
        <div className="fixed inset-0 z-0 overflow-hidden pointer-events-none">
          {/* Primary gradient blur - Dark green theme */}
          <div
            className="absolute top-0 right-0 w-[50vw] h-[50vh] blur-3xl opacity-30"
            style={{
              background: 'radial-gradient(circle at center, rgba(24, 76, 6, 0.3) 0%, rgba(24, 76, 6, 0) 70%)', // Dark green #184c06
              transform: 'translate(20%, -20%)'
            }}
          />

          {/* Secondary gradient blur */}
          <div
            className="absolute bottom-0 left-0 w-[40vw] h-[40vh] blur-3xl opacity-20"
            style={{
              background: 'radial-gradient(circle at center, rgba(34, 147, 2, 0.3) 0%, rgba(34, 147, 2, 0) 70%)', // Light green #229302
              transform: 'translate(-20%, 20%)'
            }}
          />

          {/* Subtle dot grid pattern */}
          <div
            className="absolute inset-0 opacity-[0.015]"
            style={{
              backgroundImage: 'radial-gradient(rgba(34, 147, 2, 0.2) 1px, transparent 1px)', // Light green #229302
              backgroundSize: '40px 40px'
            }}
          />
        </div>

        <div className="container mx-auto px-4 md:px-6 relative z-10">
          <motion.div
            style={{ opacity: headerOpacity }}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl mx-auto text-center mb-20"
          >
            <Badge
              variant="outline"
              className="px-4 py-1.5 mb-6 bg-green-50 border-green-100 text-green-800 text-sm font-medium dark:bg-green-900/20 dark:border-green-800/30 dark:text-green-400"
            >
              {content.lastUpdated}
            </Badge>
            <h1 className="text-4xl md:text-6xl font-medium tracking-tight text-gray-900 dark:text-gray-100 mb-6 leading-tight">
              {content.title}
              <br />
              <span className="bg-gradient-to-r from-[#184c06] via-[#1b6504] to-[#229302] bg-clip-text text-transparent font-semibold">
                {content.titleHighlight}
              </span>
            </h1>
            <p className="text-lg md:text-xl text-gray-500 dark:text-gray-400 mb-8 max-w-3xl mx-auto leading-relaxed">
              {content.subtitle}
            </p>
          </motion.div>

          {/* Overview Section */}
          <div className="relative mb-16 z-10">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="max-w-4xl mx-auto"
            >
              <Card className="p-8 mb-8 bg-white/80 backdrop-blur-sm dark:bg-gray-800/80">
                <p className="text-xl md:text-2xl font-medium text-[#184c06] dark:text-green-400 mb-6">
                  {content.quote}
                </p>
                <div className="prose prose-lg max-w-none text-gray-600 dark:text-gray-400">
                  <p className="mb-6 text-lg leading-relaxed">
                    {content.overview.paragraph1}
                  </p>
                  <p className="mb-6 text-lg leading-relaxed">
                    {content.overview.paragraph2}
                  </p>
                </div>
              </Card>
            </motion.div>
          </div>





          {/* Key Features Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="relative mb-16 z-10 max-w-4xl mx-auto"
          >
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Card className="p-6 hover:shadow-md transition-shadow duration-300 bg-white/90 dark:bg-gray-800/90">
                <div className="flex items-center justify-center w-12 h-12 rounded-full bg-green-50 dark:bg-green-900/20 mb-4">
                  <UserCheck className="h-6 w-6 text-[#229302]" />
                </div>
                <h3 className="text-xl font-semibold mb-2 text-gray-900 dark:text-gray-100">
                  {content.features.transparency.title}
                </h3>
                <p className="text-gray-600 dark:text-gray-400">
                  {content.features.transparency.description}
                </p>
              </Card>

              <Card className="p-6 hover:shadow-md transition-shadow duration-300 bg-white/90 dark:bg-gray-800/90">
                <div className="flex items-center justify-center w-12 h-12 rounded-full bg-green-50 dark:bg-green-900/20 mb-4">
                  <Server className="h-6 w-6 text-[#229302]" />
                </div>
                <h3 className="text-xl font-semibold mb-2 text-gray-900 dark:text-gray-100">
                  {content.features.infrastructure.title}
                </h3>
                <p className="text-gray-600 dark:text-gray-400">
                  {content.features.infrastructure.description}
                </p>
              </Card>

              <Card className="p-6 hover:shadow-md transition-shadow duration-300 bg-white/90 dark:bg-gray-800/90">
                <div className="flex items-center justify-center w-12 h-12 rounded-full bg-green-50 dark:bg-green-900/20 mb-4">
                  <Shield className="h-6 w-6 text-[#229302]" />
                </div>
                <h3 className="text-xl font-semibold mb-2 text-gray-900 dark:text-gray-100">
                  {content.features.compliance.title}
                </h3>
                <p className="text-gray-600 dark:text-gray-400">
                  {content.features.compliance.description}
                </p>
              </Card>
            </div>
          </motion.div>

          {/* Detailed Policy Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="relative mb-16 z-10 max-w-4xl mx-auto"
          >
            <Card className="p-8 bg-white/90 dark:bg-gray-800/90">
              <div className="mb-8">
                <div className="flex items-center mb-4">
                  <Lock className="h-5 w-5 text-[#229302] mr-2" />
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                    {content.detailed.access.title}
                  </h3>
                </div>
                <p className="text-gray-600 dark:text-gray-400 mb-4">
                  {content.detailed.access.description}
                </p>
              </div>

              <div className="mb-8">
                <div className="flex items-center mb-4">
                  <FileText className="h-5 w-5 text-[#229302] mr-2" />
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                    {content.detailed.policy.title}
                  </h3>
                </div>
                <ul className="list-disc pl-5 text-gray-600 dark:text-gray-400 space-y-2">
                  {content.detailed.policy.points.map((point, index) => (
                    <li key={index}>{point}</li>
                  ))}
                </ul>
              </div>
            </Card>
          </motion.div>

          {/* Privacy Policy PDF Download Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="relative mb-16 z-10 max-w-4xl mx-auto"
          >
            <div className="grid grid-cols-1 gap-4 max-w-md mx-auto">
              <Card className="p-4 bg-white/90 dark:bg-gray-800/90 hover:shadow-lg transition-all duration-300 border-green-100 dark:border-green-900/20">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center">
                    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-green-50 dark:bg-green-900/20 mr-2">
                      <Shield className="h-4 w-4 text-[#229302]" />
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                        Privacy Policy (EN)
                      </h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400">English Version</p>
                    </div>
                  </div>
                  <Download className="h-3 w-3 text-[#229302]" />
                </div>
                <a
                  href="/PrivacyPolicyEN.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full"
                >
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full border-green-200 hover:bg-green-50 hover:border-green-300 dark:border-green-800 dark:hover:bg-green-900/20"
                  >
                    Download PDF
                  </Button>
                </a>
              </Card>
            </div>
          </motion.div>

          {/* Call to Action */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="relative mb-16 z-10 max-w-4xl mx-auto text-center"
          >
            <Card className="p-8 border-green-200 bg-gradient-to-br from-green-50 to-white dark:from-gray-800 dark:to-gray-900 dark:border-green-900/20">
              <h3 className="text-2xl font-semibold mb-4 text-gray-900 dark:text-gray-100">
                {content.cta.title}
              </h3>
              <p className="text-lg text-gray-600 dark:text-gray-400 mb-6 max-w-2xl mx-auto">
                {content.cta.description}
              </p>

              <Link to="/contact" className="inline-block mx-auto">
                <Button
                  variant="default"
                  size="lg"
                  className="bg-[#184c06] hover:bg-[#229302] text-white w-auto px-6"
                >
                  {content.cta.button} <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </Card>
          </motion.div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default PrivacyPage;
